import { call, takeEvery, select } from 'redux-saga/effects';
import { BankStatementsConstants } from 'constants/bankStatementsConstants';
import { request } from 'components/Fetch';
import { get } from 'lodash';

const ENDPOINTS = {
  GET_STATEMENTS: (entityType, entityId) => `/bank-statement-processor/${entityType}/${entityId}/statements`,
  GET_STATEMENT: (entityType, entityId, statementId) => `/bank-statement-processor/${entityType}/${entityId}/statements/${statementId}`,
  GET_CONFIDENCE_THRESHOLD: '/bank-statement-processor/settings/confidence-threshold',
  GET_PDF_DATA: (entityType, entityId, statementId) => `/bank-statement-processor/${entityType}/${entityId}/statements/${statementId}/file`,
  DELETE_STATEMENT: (entityType, entityId, statementId) => `/bank-statement-processor/${entityType}/${entityId}/statements/${statementId}/delete`,
  RESTORE_STATEMENT: (entityType, entityId, statementId) => `/bank-statement-processor/${entityType}/${entityId}/statements/${statementId}/restore`,
  ADD_TRANSACTION: (entityType, entityId, statementId, accountId) => `/bank-statement-processor/${entityType}/${entityId}/statements/${statementId}/accounts/${accountId}/transactions`,
  DELETE_TRANSACTION: (entityType, entityId, statementId, accountId, transactionId) => `/bank-statement-processor/${entityType}/${entityId}/statements/${statementId}/accounts/${accountId}/transactions/${transactionId}`,
  UPDATE_TRANSACTION: (entityType, entityId, statementId, transactionId) => `/bank-statement-processor/${entityType}/${entityId}/statements/${statementId}/transactions/${transactionId}`,
  GET_ACCOUNT: (entityType, entityId, statementId, accountId) => `/bank-statement-processor/${entityType}/${entityId}/statements/${statementId}/accounts/${accountId}`,
  RECONCILE_STATEMENT_ACCOUNT: (entityType, entityId, statementId, accountId) => `/bank-statement-processor/${entityType}/${entityId}/statements/${statementId}/accounts/${accountId}/reconcile`,
}

function* addDataVersionHeaderRequest(requestConfig, action) {
  const dataVersion = yield select(state => get(state, 'bankStatements.dataVersion'));

  yield call(request({
    ...requestConfig,
    headers: {
      ...requestConfig.headers,
      'X-LUSA-Data-Version': dataVersion,
    },
  }), action);
}

function* getStatements(action) {
  const { entityType, entityId, bankProcessorKey } = action.payload;
  yield call(request({
    type: BankStatementsConstants.GET_STATEMENTS,
    method: 'GET',
    url: `${ENDPOINTS.GET_STATEMENTS(entityType, entityId)}?key=${bankProcessorKey}`,
  }), action);
}

function* getStatement(action) {
  const { entityType, entityId, bankProcessorKey, statementId } = action.payload;
  yield call(request({
    type: BankStatementsConstants.GET_STATEMENT,
    method: 'GET',
    url: `${ENDPOINTS.GET_STATEMENT(entityType, entityId, statementId)}?key=${bankProcessorKey}`,
  }), action);
}

function* processStatement(action) {
  const { entityType, entityId, bankProcessorKey } = action.payload;
  yield addDataVersionHeaderRequest({
    type: BankStatementsConstants.PROCESS_STATEMENT,
    method: 'POST',
    url: `${ENDPOINTS.GET_STATEMENTS(entityType, entityId)}?key=${bankProcessorKey}`,
  }, action);
}

function* getConfidenceThreshold(action) {
  const { bankProcessorKey } = action.payload;
  yield call(request({
    type: BankStatementsConstants.GET_CONFIDENCE_THRESHOLD,
    method: 'GET',
    url: `${ENDPOINTS.GET_CONFIDENCE_THRESHOLD}?key=${bankProcessorKey}`,
  }), action);
}

function* getPdfFile(action) {
  const { entityType, entityId, bankProcessorKey, statementId } = action.payload;
  yield call(request({
    type: BankStatementsConstants.GET_PDF_FILE,
    method: 'GET',
    responseType: 'arraybuffer',
    url: `${ENDPOINTS.GET_PDF_DATA(entityType, entityId, statementId)}?key=${bankProcessorKey}`,
  }), action);
}

function* deleteStatement(action) {
  const { entityType, entityId, statementId, bankProcessorKey } = action.payload;
  yield addDataVersionHeaderRequest({
    type: BankStatementsConstants.DELETE_STATEMENT,
    method: 'PUT',
    url: `${ENDPOINTS.DELETE_STATEMENT(entityType, entityId, statementId)}?key=${bankProcessorKey}`,
  }, action);
}

function* restoreStatement(action) {
  const { entityType, entityId, statementId, bankProcessorKey } = action.payload;
  yield addDataVersionHeaderRequest({
    type: BankStatementsConstants.RESTORE_STATEMENT,
    method: 'PUT',
    url: `${ENDPOINTS.RESTORE_STATEMENT(entityType, entityId, statementId)}?key=${bankProcessorKey}`,
  }, action);
}

function* updateTransaction(action) {
  const { entityType, entityId, statementId, transactionId, bankProcessorKey } = action.payload;
  yield addDataVersionHeaderRequest({
    type: BankStatementsConstants.UPDATE_TRANSACTION,
    method: 'PUT',
    url: `${ENDPOINTS.UPDATE_TRANSACTION(entityType, entityId, statementId, transactionId)}?key=${bankProcessorKey}`,
  }, action);
}

function* updateAccount(action) {
  const { entityType, entityId, statementId, accountId, bankProcessorKey } = action.payload;
  yield addDataVersionHeaderRequest({
    type: BankStatementsConstants.UPDATE_ACCOUNT,
    method: 'PUT',
    url: `${ENDPOINTS.GET_ACCOUNT(entityType, entityId, statementId, accountId)}?key=${bankProcessorKey}`,
  }, action);
}

function* deleteTransaction(action) {
  const { entityType, entityId, statementId, transactionId, accountId, bankProcessorKey } = action.payload;
  yield addDataVersionHeaderRequest({
    type: BankStatementsConstants.DELETE_TRANSACTION,
    method: 'DELETE',
    url: `${ENDPOINTS.DELETE_TRANSACTION(entityType, entityId, statementId, accountId, transactionId)}?key=${bankProcessorKey}`,
  }, action);
}

function* addTransaction(action) {
  const { entityType, entityId, statementId, accountId, bankProcessorKey } = action.payload;
  yield addDataVersionHeaderRequest({
    type: BankStatementsConstants.ADD_TRANSACTION,
    method: 'POST',
    url: `${ENDPOINTS.ADD_TRANSACTION(entityType, entityId, statementId, accountId)}?key=${bankProcessorKey}`,
  }, action);
}

function* updateStatement(action) {
  const { entityType, entityId, statementId, bankProcessorKey } = action.payload;
  yield addDataVersionHeaderRequest({
    type: BankStatementsConstants.UPDATE_STATEMENT,
    method: 'PUT',
    url: `${ENDPOINTS.GET_STATEMENT(entityType, entityId, statementId)}?key=${bankProcessorKey}`,
  }, action);
}

function* reconcileStatementAccount(action) {
  const { entityType, entityId, statementId, accountId, bankProcessorKey } = action.payload;
  yield addDataVersionHeaderRequest({
    type: BankStatementsConstants.RECONCILE_STATEMENT_ACCOUNT,
    method: 'POST',
    url: `${ENDPOINTS.RECONCILE_STATEMENT_ACCOUNT(entityType, entityId, statementId, accountId)}?key=${bankProcessorKey}`,
  }, action);
}

export default function* () {
  yield takeEvery(BankStatementsConstants.GET_STATEMENTS, getStatements);
  yield takeEvery(BankStatementsConstants.GET_STATEMENT, getStatement);
  yield takeEvery(BankStatementsConstants.PROCESS_STATEMENT, processStatement);
  yield takeEvery(BankStatementsConstants.GET_CONFIDENCE_THRESHOLD, getConfidenceThreshold);
  yield takeEvery(BankStatementsConstants.GET_PDF_FILE, getPdfFile);
  yield takeEvery(BankStatementsConstants.DELETE_STATEMENT, deleteStatement);
  yield takeEvery(BankStatementsConstants.RESTORE_STATEMENT, restoreStatement);
  yield takeEvery(BankStatementsConstants.UPDATE_TRANSACTION, updateTransaction);
  yield takeEvery(BankStatementsConstants.UPDATE_ACCOUNT, updateAccount);
  yield takeEvery(BankStatementsConstants.DELETE_TRANSACTION, deleteTransaction);
  yield takeEvery(BankStatementsConstants.ADD_TRANSACTION, addTransaction);
  yield takeEvery(BankStatementsConstants.UPDATE_STATEMENT, updateStatement);
  yield takeEvery(BankStatementsConstants.RECONCILE_STATEMENT_ACCOUNT, reconcileStatementAccount);
}
